import useStuckWatchdog from './useStuckWatchdog';

/**
 * Budget for the whole start sequence: create the conversation doc, call /agent, open the
 * proxy WebSocket, and hear back that the agent is live. A healthy start takes ~2s in
 * production, so this is generous by an order of magnitude.
 *
 * It is deliberately bracketed on both sides:
 *  - above CONVERSATION_DOC_TIMEOUT_MS (15s), so a stalled Firestore write reports its own
 *    specific error rather than being swallowed by the catch-all recovery;
 *  - below the 60s ElevenLabs "no user message received" cutoff, so the learner is told
 *    something is wrong before the agent quietly hangs up on them.
 */
export const STUCK_REALTIME_START_TIMEOUT_MS = 30000;

/**
 * Last-resort recovery for a realtime session that never became usable.
 *
 * `sharedState.loading` is latched the moment the learner presses start and is only
 * released when the proxy confirms the agent is live. Until then the conversation screen
 * shows the activity-generation loader and the mic button is disabled — so any stall in
 * between reads as "the app is still preparing my exercise" and the learner waits.
 *
 * They can wait forever. On 2026-08-13 a learner sat on that loader while the agent on the
 * other end sat waiting for audio that a dead mic pipeline was never going to send; sixty
 * seconds later ElevenLabs hung up and nothing on screen changed. Their retry stalled
 * before it even opened a socket, so there was no error to catch and no event to alert on.
 *
 * Armed from the start handler rather than by watching `loading`, so it guards only the
 * start sequence — not the other flows that share the flag.
 *
 * @param {object} params
 * @param {boolean} params.loading - The shared loading latch; falling false disarms.
 * @param {(stuckForMs: number) => void} params.onStuck - Called once when the start never completed.
 * @returns {() => void} arm - Call when the start sequence begins.
 */
export default function useRealtimeStartWatchdog({ loading, onStuck }) {
  return useStuckWatchdog({
    active: loading,
    timeoutMs: STUCK_REALTIME_START_TIMEOUT_MS,
    onStuck,
  });
}
